import { forwardRef, ReactNode, Ref, useMemo, MouseEvent, PropsWithChildren } from 'react';
import { Avatar } from 'flowbite-react';
import { Menu as HeadlessMenu } from '@headlessui/react';

import { NavLinkType, openInNewTab, useNavigationContext, useResponsiveBreakPoints, cn } from '@restworld/utils-common';
const placeholderAvatar = (
  <Avatar alt="" onClick={() => openInNewTab(`/profilo`)} placeholderInitials="" size="md" rounded />
);
export const Dropdown = ({
  menuItemList,
  buttonEl,
  onClick,
  selectedValue,
  children,
  headerItemsCustomStyles,
}: PropsWithChildren<DropdownProps>) => (
  <HeadlessMenu as="div" className="relative inline-block">
    <HeadlessMenu.Button
      className={`inline-flex w-full justify-center rounded-md bg-opacity-20 py-2 text-sm font-medium text-white hover:bg-opacity-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 z-[1.5] ${
        !buttonEl ? 'bg-black' : ''
      }`}
    >
      {buttonEl || placeholderAvatar}
    </HeadlessMenu.Button>
    {menuItemList?.length || children ? (
      <HeaderItemsStyled headerItemsCustomStyles={headerItemsCustomStyles}>
        <div className="px-1 py-1">
          {menuItemList
            ? menuItemList.map((item) => (
                <MenuItem key={item.key} item={item} selectedValue={selectedValue} onClick={onClick} />
              ))
            : children}
        </div>
      </HeaderItemsStyled>
    ) : null}
  </HeadlessMenu>
);

const HeaderItemsStyled = ({ headerItemsCustomStyles, className, ...props }: any) => (
  <HeadlessMenu.Items
    className={cn(
      'min-w-full absolute top-[48px] right-0 mt-4 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-10',
      headerItemsCustomStyles,
      className
    )}
    {...props}
  />
);

export const NextLink = forwardRef(
  (
    props: {
      href: string;
      children: ReactNode;
      active?: boolean;
      onClick?: (e?: MouseEvent<HTMLAnchorElement, globalThis.MouseEvent>) => void;
      selected?: boolean;
      neutral?: boolean;
    },
    ref: Ref<HTMLButtonElement> | undefined
  ) => {
    const { linkComponent: Link } = useNavigationContext();
    const { href, children, active, onClick: handleClick, selected, neutral, ...rest } = props;
    const { isDesktop } = useResponsiveBreakPoints();
    const activeClassFn = useMemo(
      () =>
        `${
          active
            ? 'bg-purple-500 text-white'
            : selected
            ? 'bg-purple-100 text-gray-900'
            : neutral
            ? 'bg-white'
            : 'text-gray-900'
        } group flex w-full items-center rounded-md px-2 py-2 text-sm`,
      [active, neutral, selected]
    );
    return (
      <Link
        href={href}
        target={isDesktop && href !== '#!' ? '_blank' : '_self'}
        onClick={(e) => {
          if (handleClick) handleClick(e);
        }}
      >
        <button className={activeClassFn} ref={ref} {...rest}>
          {children}
        </button>
      </Link>
    );
  }
);
type DropdownProps = {
  menuItemList?: NavLinkType[];
  buttonEl?: ReactNode;
  onClick?: (x?: string | number) => void;
  selectedValue?: string | number;
  headerItemsCustomStyles?: string;
};

type SelectedValueType = string | number;
interface MenuItemProps {
  item: NavLinkType;
  selectedValue?: SelectedValueType;
  onClick?: (x?: SelectedValueType) => void;
}

function MenuItem({ item, selectedValue, onClick, children }: PropsWithChildren<MenuItemProps>) {
  const isCustomItem = !!children;

  return (
    <HeadlessMenu.Item key={item.key}>
      {({ active }) => (
        <NextLink
          selected={isCustomItem ? undefined : selectedValue === item.value}
          href={item.url ? item.url : '#!'}
          onClick={() => {
            !!onClick && onClick(item.value);
            item?.onClick?.();
          }}
          neutral={isCustomItem}
          active={isCustomItem ? undefined : active}
        >
          <div className="min-w-[max-content]">{children || item.label}</div>
        </NextLink>
      )}
    </HeadlessMenu.Item>
  );
}

Dropdown.MenuItem = MenuItem;
